import { FormattedMessage } from 'react-intl';
// @ts-ignore
import LazyLoad from 'react-lazyload';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { Context } from '@alltrails/shared/types/context';
import InviteYourFriendsBanner from 'components/InviteYourFriendsBanner';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useDisplayMetric from 'hooks/useDisplayMetric';
import * as styles from './styles/styles.module.scss';
import GenericGiftingBlock from '../blocks/GiftingBlock/GenericGiftingBlock';
import ShopifyPreview from '../blocks/ShopifyPreview';
import OnePercentBlock from '../blocks/OnePercentBlock';
import EditorsChoiceBlock from '../blocks/EditorsChoiceBlock';
import ExploreAndDiscover from '../blocks/ExploreAndDiscover';
import CommunityStats from '../blocks/CommunityStats/CommunityStats';
import TrailRiver from '../blocks/TrailRiver';
import SignupActivitySuitability from '../blocks/SignupActivitySuitability';
import useListItems from '../../hooks/useListItems';
import useSections from '../../hooks/useSections';
import ProUpsell from '../blocks/ProUpsell';
import ProUpsellNoAuth from '../blocks/ProUpsellNoAuth';
import ActivitiesRiver from '../blocks/ActivitiesRiver';
import SeoTrailTabbed from '../blocks/SeoTrailTabbed';
import { wrapUrlSafe } from '../../utils/language_support_util';
import {
  ATTRIBUTE,
  COMMUNITY_STATS,
  EDITORS_CHOICE_APP,
  EXPLORE_AND_DISCOVER,
  GIFTING_UPSELL,
  MERCHANDISE_STORE,
  ON_TRACK_WITH_PRO_CHECKLIST,
  ON_TRACK_WITH_PRO,
  ONE_PERCENT_PLANET,
  SEO_TRAILS,
  SIGNUP_ACTIVITY_SUITABILITY,
  TRAIL_LIST
} from './constants';
import { US } from '../../constants/CountryCode';

type Props = {
  context: Context;
  initialSections: {
    section_type?: string;
  }[];
  windowWidth?: number;
  lists: {
    listItems?: object;
    lists?: {
      id?: number;
      title?: string;
    }[];
  };
  locationData: {
    city: {
      names: { [key: string]: any[] };
    };
    country: string;
    latLng: (string | number)[];
  };
};

enum SectionType {
  Attribute = 'attribute',
  InviteYourFriends = 'invite_your_friends'
}

const SectionSwitch = ({ context, initialSections, windowWidth, lists, locationData }: Props) => {
  const displayMetric = useDisplayMetric();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const { listMethods, renderModal } = useListItems(lists);
  const sections = useSections(initialSections, locationData.latLng, context);

  const trailClass = (idx: number) => {
    // we need to update spacing here if there are two trails that are sequential.
    const prevSection = sections[idx - 1];
    let classes = '';
    if (user && idx === 0) {
      classes += `${styles.authenticatedSpacing} `;
    }

    if (prevSection && prevSection.section_type === TRAIL_LIST) {
      classes += styles.secondTrailPadding;
    } else {
      classes += styles.trailRiverPadding;
    }

    return classes;
  };

  const trailRiverTitle = (section: any, idx: any) => {
    if (idx === 0 && locationData.city) {
      const shortRegionCode = languageRegionCode.split('-')[0];

      return (
        <>
          <FormattedMessage defaultMessage="Local favorites near" />{' '}
          <a className="xlate-none" href={wrapUrlSafe('/explore?ref=homepage_explore_trailriver', languageRegionCode)}>
            {locationData.city.names[shortRegionCode]}
          </a>
        </>
      );
    }

    return section.title;
  };

  return (
    <>
      {renderModal()}
      {sections.map((section: any, idx: number) => {
        switch (section.section_type) {
          case SectionType.InviteYourFriends:
            return <InviteYourFriendsBanner key={section.section_type} />;
          case ATTRIBUTE:
            return (
              <ActivitiesRiver
                key={section.section_type}
                title={section.title}
                activities={section.objects}
                windowWidth={windowWidth}
                languageRegionCode={languageRegionCode}
              />
            );
          case COMMUNITY_STATS:
            return <CommunityStats key={section.section_type} curatedTrails={350} fellowExplorers={40} loggedDistanceMeters={965000} />;
          case ON_TRACK_WITH_PRO_CHECKLIST:
            return <ProUpsell key={section.section_type} windowWidth={windowWidth} />;
          case ON_TRACK_WITH_PRO:
            return <ProUpsellNoAuth key={section.section_type} />;
          case GIFTING_UPSELL:
            return <GenericGiftingBlock key={section.section_type} />;
          case MERCHANDISE_STORE:
            return (
              locationData.country === US &&
              languageRegionCode === LanguageRegionCode.English && (
                <LazyLoad key={section.section_type}>
                  <ShopifyPreview shopifyProducts={section.products} windowWidth={windowWidth} key={section.section_type} />
                </LazyLoad>
              )
            );
          case ONE_PERCENT_PLANET:
            return <OnePercentBlock key={section.section_type} />;
          case EDITORS_CHOICE_APP:
            return <EditorsChoiceBlock windowWidth={windowWidth} languageRegionCode={languageRegionCode} key={section.section_type} />;
          case EXPLORE_AND_DISCOVER:
            return <ExploreAndDiscover key={section.section_type} windowWidth={windowWidth} languageRegionCode={languageRegionCode} />;
          case SEO_TRAILS:
            return (
              <SeoTrailTabbed key={section.section_type} languageRegionCode={languageRegionCode} data={section.data} displayMetric={displayMetric} />
            );
          case SIGNUP_ACTIVITY_SUITABILITY:
            return (
              <SignupActivitySuitability
                activities={section.objects}
                key={section.section_type}
                mobileBrowser={__AT_DATA__.mobileBrowser}
                windowWidth={windowWidth}
                languageRegionCode={languageRegionCode}
              />
            );
          case TRAIL_LIST:
            return (
              <TrailRiver
                {...section}
                context={context}
                windowWidth={windowWidth}
                listMethods={listMethods}
                className={trailClass(idx)}
                locationData={locationData}
                title={trailRiverTitle(section, idx)}
                key={section.title}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

SectionSwitch.defaultProps = {
  windowWidth: null
};

export default SectionSwitch;
